import { IconInfoCircleFilled } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { format, parseISO } from 'date-fns';
import classNames from 'classnames';
import { Row, Col } from 'reactstrap';
import { NSButton, NSCard, NSLabel, NSSelect } from 'bricks';
import { customStyles } from 'bricks/NSSelect/NSSelect';
import NSInputNumberStepper from 'bricks/NSInputNumberStepper/NSInputNumberStepper';
import './OperatingInformation.scss';
import { IOperatingInfo, IPermanentDebtInfo, IPatchOperatingInfo } from 'views/ProFormaTable/types';
import RenderIf from 'components/RenderIf/RenderIf';
import {
    ProFormaModelIds,
    IncomeCalculationType,
    ACADEMIC_YEAR_START_MONTH_OPTIONS,
    DEFAULT_ACADEMIC_YEAR_START_MONTH,
} from 'constants/proForma';
import { NSButtonBase } from 'bricks/NSButton/NSButton';
import {
    LEASE_UP_PACE_TOOLTIP,
    OCCUPANCY_UPON_STABILIZATION,
    OPERATION_START_TOOLTIP,
    OPS_DURING_CONSTRUCTION_WARNING,
    PRO_FORMA_TABLE_NAV,
    PRO_FORMA_TABS,
    ProjectActualsDiscrepanciesEnum,
} from 'views/ProFormaTable/constants';
import IconWithTooltip from 'components/IconWithTooltip/IconWithTooltip';
import { IOptionAnyValue } from 'bricks/types';
import MonthlyAnalysisTable, { IMonthlyAnalysisPeriod } from './MonthlyAnalysisTable/MonthlyAnalysisTable';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import { getDebtFundingWindow, getMonthlyAnalysisPeriods, getStabilizedMonthMissingEntity } from './helpers';
import MetricOutputCard from '../../../MetricOutputCard/MetricOutputCard';

export interface IOperatingInformationProps {
    signUpMode?: boolean;
    navigateToSection: (sideNavName: string, tabName?: string) => void;
}

const OperatingInformation = ({ signUpMode = false, navigateToSection }: IOperatingInformationProps) => {
    const {
        proForma, operatingInfo, operatingPeriodCashFlows, handleUpdateOperatingInfo,
        cashFlowPeriods, shouldUseProjectActuals, projectActuals, uses, sources, sourceTypes,
    } = useProFormaSocketContext();
    const isIncomeCalculationTypeEstimate = operatingInfo?.incomeCalculationType === IncomeCalculationType.ESTIMATE;
    const isApartment = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proForma.proFormaModelId);
    const isApartmentWithRetail = proForma.proFormaModelId === ProFormaModelIds.APARTMENT_WITH_RETAIL;
    const isStudentHousing = proForma.proFormaModelId === ProFormaModelIds.STUDENT_HOUSING;

    const [operatingInfoData, setOperatingInfoData] = useState<Partial<IOperatingInfo & IPermanentDebtInfo>>({});
    const [operationsStartYear, setOperationsStartYear] = useState<number>(
        operatingInfo.operationStartDate
            ? parseISO(operatingInfo.operationStartDate).getFullYear()
            : cashFlowPeriods.length > 0 ? parseISO(cashFlowPeriods[0]).getFullYear() : new Date().getFullYear()
    );

    const currentMonth = operatingInfo.operationStartDate ? parseISO(operatingInfo.operationStartDate).getMonth() + 1 : null;
    // Check if the current month is within the allowed academic months (8, 9, 10)
    const isValidAcademicMonth = currentMonth !== null && ACADEMIC_YEAR_START_MONTH_OPTIONS.some(option => option.value === currentMonth);

    const [academicYearStartMonth, setAcademicYearStartMonth] = useState<number | null>(() => {
        if (operatingInfo.operationStartDate) {
            return isValidAcademicMonth ? currentMonth : DEFAULT_ACADEMIC_YEAR_START_MONTH;
        }
        return DEFAULT_ACADEMIC_YEAR_START_MONTH;
    });

    const labelClassName = 'pl-0 OperatingInformation__label-col font-weight-normal text-dark align-self-center';
    const labelClassNameNoWrap = 'pl-0 OperatingInformation__label-col my-auto font-weight-normal text-dark text-nowrap';

    const actualsDiscrepancies = projectActuals?.projectActuals?.discrepancies || [];

    const updateOperatingInfoKeys = (keyValues: Partial<IPatchOperatingInfo>) => {
        if (operatingInfo?.id) {
            handleUpdateOperatingInfo(operatingInfo.id, keyValues);
        }
    };

    const monthlyAnalysisPeriods: IMonthlyAnalysisPeriod[] = getMonthlyAnalysisPeriods(
        proForma, cashFlowPeriods, operatingInfo, operatingPeriodCashFlows, operatingInfo.saleMonth,
    );

    const redirectToDevelopmentTab = () => {
        navigateToSection(PRO_FORMA_TABLE_NAV.PRO_FORMA.name, PRO_FORMA_TABS.DEVELOPMENT.name);
    };

    const stabilizedMonthMissingEntity = getStabilizedMonthMissingEntity(proForma, isIncomeCalculationTypeEstimate);

    const developmentEndMonth = uses?.developmentEndMonth ?? 0;
    const saleMonth = operatingInfo.saleMonth ?? cashFlowPeriods.length;
    const { firstDebtMonth } = getDebtFundingWindow(sources, sourceTypes);
    const opStartFloor = (firstDebtMonth ?? developmentEndMonth) + 1;
    const operationStartPeriods = cashFlowPeriods.slice(opStartFloor, saleMonth);

    const operationStartDateOptions = operationStartPeriods.map((periodDate, index) => ({
        label: `${format(parseISO(periodDate), 'MMM yyyy')} (Month ${opStartFloor + index})`,
        value: periodDate,
    }));

    const showOpsDuringConstructionWarning = operatingInfo?.operationBeginMonth != null
        && operatingInfo.operationBeginMonth <= developmentEndMonth;

    const operationStartYearOptions = Array.from(
        new Set(operationStartPeriods.map(periodDate => parseISO(periodDate).getFullYear()))
    ).map(year => ({
        label: year.toString(),
        value: year
    }));

    const operationStartDatePlaceholder = operatingInfo?.operationStartDate && operatingInfo?.operationBeginMonth
        ? `${format(parseISO(operatingInfo.operationStartDate), 'MMM yyyy')} (Month ${operatingInfo.operationBeginMonth})` : '';

    const handleOperationStartDateSelect = (option: IOptionAnyValue) => {
        if (operatingInfo?.id) {
            handleUpdateOperatingInfo(operatingInfo.id, { operationStartDate: option.value });
        }
    };

    const handleAcademicOperationStartDateSelect = useCallback((
        yearOption?: IOptionAnyValue | null,
        monthOption?: IOptionAnyValue | null,
        skipUndoRedo = false,
    ) => {
        if (operatingInfo?.id) {
            // Use provided values or fallback to state defaults
            const selectedYear = yearOption?.value ?? operationsStartYear;
            const selectedMonth = monthOption?.value ?? academicYearStartMonth;

            if (selectedYear !== null && selectedMonth !== null) {
                // converts month to 2-digit format with leading zero if necessary
                const month = String(selectedMonth).padStart(2, '0');
                const operationStartDate = `${selectedYear}-${month}-01`;

                handleUpdateOperatingInfo(operatingInfo.id, { operationStartDate }, skipUndoRedo);
            }
        }
    }, [operatingInfo?.id, operationsStartYear, academicYearStartMonth, handleUpdateOperatingInfo]);

    useEffect(() => {
        if (operatingInfo) {
            setOperatingInfoData({
                occupancyUponStabilizationPercentage: operatingInfo.occupancyUponStabilizationPercentage,
                rentalGrowthPercentage: operatingInfo.rentalGrowthPercentage,
                percentPreLeased: operatingInfo.percentPreLeased,
                leaseUpPace: operatingInfo.effectiveLeaseUpPace,
                userPaceReachesStabilization: operatingInfo.userPaceReachesStabilization,
                lengthOfLease: operatingInfo.lengthOfLease,
                initialFreeRent: operatingInfo.initialFreeRent,
                stabilizedFreeRent: operatingInfo.stabilizedFreeRent,
            });
        }
    }, [operatingInfo, operatingPeriodCashFlows]);

    const correctedForDateRef = useRef<string | null>(null);

    // Update operation start date if it's outside the allowed academic month range
    useEffect(() => {
        if (
            isStudentHousing 
            && operatingInfo.operationStartDate 
            && !isValidAcademicMonth 
            && correctedForDateRef.current !== operatingInfo.operationStartDate
        ) {
            correctedForDateRef.current = operatingInfo.operationStartDate;
            // Update to default academic month (August)
            // Pass skipUndoRedo: true to prevent this auto-correction from being tracked in undo/redo history
            handleAcademicOperationStartDateSelect(
                null,
                { value: DEFAULT_ACADEMIC_YEAR_START_MONTH, label: 'August' },
                true,
            );
        }
    }, [isStudentHousing, operatingInfo.operationStartDate, isValidAcademicMonth, handleAcademicOperationStartDateSelect]);

    return (
        <Row className="mx-n1 px-3">
            <Col className="px-1 my-3" sm={12} md="auto">
                <RenderIf isTrue={!isStudentHousing}>
                    <div className="d-flex align-items-center">
                        <div className={`d-flex my-auto ${labelClassName}`}>
                            <NSLabel className="my-auto font-weight-normal text-dark mr-1">Occupancy upon stabilization</NSLabel>
                            <IconWithTooltip
                                name="occupancyUponStabilization"
                                icon={IconInfoCircleFilled}
                                tooltipText={OCCUPANCY_UPON_STABILIZATION}
                            />
                        </div>
                        <NSInputNumberStepper
                            percentage
                            name="occupancyUponStabilizationPercentage"
                            value={operatingInfoData.occupancyUponStabilizationPercentage || null}
                            onChange={(value: number) => updateOperatingInfoKeys({
                                occupancyUponStabilizationPercentage: value,
                            })}
                            min={0}
                            step={1}
                            max={100}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>
                </RenderIf>
                <RenderIf isTrue={(!isApartment && !isStudentHousing) || isApartmentWithRetail}>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={`my-auto ${labelClassName}`}>
                            {isApartmentWithRetail ? 'Retail income' : 'Income calculation'}
                        </NSLabel>
                        <div className="my-auto font-weight-normal text-dark text-nowrap OperatingInformation__income-buttons">
                            {
                                signUpMode ? (
                                    <>
                                        <NSButtonBase
                                            color={isIncomeCalculationTypeEstimate ? 'primary' : 'secondary'}
                                            outline={false}
                                            callback={() => updateOperatingInfoKeys({ incomeCalculationType: IncomeCalculationType.ESTIMATE })}
                                            text="Allocation"
                                            className={classNames({ 'border border-primary bg-primary-lighten': false })}
                                        />
                                        <NSButtonBase
                                            color={isIncomeCalculationTypeEstimate ? 'secondary' : 'primary'}
                                            outline={false}
                                            callback={() => updateOperatingInfoKeys({ incomeCalculationType: IncomeCalculationType.TENANTS })}
                                            text="Rent roll"
                                            className={classNames({
                                                'border border-primary bg-primary-lighten': false,
                                            })}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <NSButton
                                            color={isIncomeCalculationTypeEstimate ? 'primary' : 'secondary'}
                                            outline={false}
                                            callback={() => updateOperatingInfoKeys({ incomeCalculationType: IncomeCalculationType.ESTIMATE })}
                                            text="Allocation"
                                            className={classNames({ 'border border-primary bg-primary-lighten': false })}
                                        />
                                        <NSButton
                                            color={!isIncomeCalculationTypeEstimate ? 'primary' : 'secondary'}
                                            outline={false}
                                            callback={() => updateOperatingInfoKeys({ incomeCalculationType: IncomeCalculationType.TENANTS })}
                                            text="Rent roll"
                                            className={classNames({
                                                'border border-primary bg-primary-lighten': false,
                                            })}
                                        />
                                    </>
                                )
                            }
                        </div>
                    </div>
                </RenderIf>
                <RenderIf
                    isTrue={isIncomeCalculationTypeEstimate || isApartment}
                >
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Rent growth (annual)</NSLabel>
                        <NSInputNumberStepper
                            percentage
                            name="rentalGrowthPercentage"
                            value={operatingInfoData.rentalGrowthPercentage || null}
                            onChange={(value: number) => updateOperatingInfoKeys({ rentalGrowthPercentage: value })}
                            min={0}
                            step={0.1}
                            max={100}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>
                    <RenderIf isTrue={!isStudentHousing}>
                        <div className="d-flex align-items-center mt-2">
                            <NSLabel className={labelClassNameNoWrap}>% pre-leased</NSLabel>
                            <NSInputNumberStepper
                                percentage
                                name="percentPreLeased"
                                value={operatingInfoData.percentPreLeased || null}
                                onChange={(value: number) => updateOperatingInfoKeys({ percentPreLeased: value })}
                                min={0}
                                step={1}
                                max={100}
                                inputGroupClassName="OperatingInformation__input-width"
                            />
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <div className={`d-flex my-auto ${labelClassName}`}>
                                <NSLabel className="my-auto font-weight-normal text-dark mr-1">
                                    Lease-up pace
                                    {` (${isApartment ? 'units' : 'SF'}/month)`}
                                </NSLabel>
                                <RenderIf
                                    isTrue={!operatingInfoData.userPaceReachesStabilization}
                                >
                                    <IconWithTooltip
                                        name="leaseUpPace"
                                        icon={IconInfoCircleFilled}
                                        tooltipText={LEASE_UP_PACE_TOOLTIP}
                                    />
                                </RenderIf>
                            </div>
                            <NSInputNumberStepper
                                name="leaseUpPace"
                                value={operatingInfoData.leaseUpPace || null}
                                onChange={(value: number) => updateOperatingInfoKeys({ leaseUpPace: value })}
                                min={0}
                                step={1}
                                inputGroupClassName="OperatingInformation__input-width"
                            />
                        </div>
                        <div className="d-flex align-items-center mt-2">
                            <NSLabel className={labelClassNameNoWrap}>Length of lease</NSLabel>
                            <NSInputNumberStepper
                                name="lengthOfLease"
                                value={operatingInfoData.lengthOfLease || null}
                                onChange={(value: number) => updateOperatingInfoKeys({ lengthOfLease: value })}
                                min={1}
                                step={1}
                                customSuffix={(operatingInfo.lengthOfLease || 0) > 1 ? 'months' : 'month'}
                                inputGroupClassName="OperatingInformation__input-width"
                            />
                        </div>
                    </RenderIf>
                </RenderIf>
                <RenderIf isTrue={isStudentHousing}>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Operations start year</NSLabel>
                        <NSSelect
                            name="operationsStartYear"
                            options={operationStartYearOptions}
                            value={operationStartYearOptions.find(option => {
                                if (!operatingInfo?.operationStartDate) return false;
                                const infoDate = parseISO(operatingInfo.operationStartDate);
                                return infoDate.getFullYear() === option.value;
                            }) || (operationsStartYear ? { label: operationsStartYear.toString(), value: operationsStartYear } : null)}
                            onChange={(option: IOptionAnyValue) => {
                                setOperationsStartYear(option?.value);
                                handleAcademicOperationStartDateSelect(option);
                            }}
                            placeholder="Select year"
                            isClearable={false}
                            menuPortalTarget={document.body}
                            formClassName="OperatingInformation__select-width"
                            styles={{
                                ...customStyles,
                                menuPortal: (provided: any) => ({ ...provided, zIndex: 10 }),
                            }}
                        />
                    </div>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Academic year start month</NSLabel>
                        <NSSelect
                            name="academicYearStartMonth"
                            options={ACADEMIC_YEAR_START_MONTH_OPTIONS}
                            value={ACADEMIC_YEAR_START_MONTH_OPTIONS.find(option => {
                                if (!operatingInfo?.operationStartDate) return academicYearStartMonth === option.value;
                                const infoDate = parseISO(operatingInfo.operationStartDate);
                                return infoDate.getMonth() + 1 === option.value; // Convert 0-based getMonth() to 1-based
                            }) || (academicYearStartMonth !== null
                                ? ACADEMIC_YEAR_START_MONTH_OPTIONS.find(option => option.value === academicYearStartMonth) || null
                                : null)}
                            onChange={(option: IOptionAnyValue) => {
                                setAcademicYearStartMonth(option?.value);
                                handleAcademicOperationStartDateSelect(null, option);
                            }}
                            placeholder="Select month"
                            isClearable={false}
                            menuPortalTarget={document.body}
                            formClassName="OperatingInformation__select-width"
                            styles={{
                                ...customStyles,
                                menuPortal: (provided: any) => ({ ...provided, zIndex: 10 }),
                            }}
                        />
                    </div>
                </RenderIf>
                <RenderIf isTrue={isApartment && !isStudentHousing}>
                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassNameNoWrap}>Initial free rent</NSLabel>
                        <NSInputNumberStepper
                            name="initialFreeRent"
                            value={operatingInfoData.initialFreeRent || null}
                            onChange={(value: number) => updateOperatingInfoKeys({ initialFreeRent: value })}
                            min={0}
                            step={1}
                            customSuffix={(operatingInfo.initialFreeRent || 0) > 1 ? 'months' : 'month'}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>

                    <div className="d-flex align-items-center mt-2">
                        <NSLabel className={labelClassName}>Stabilized free rent</NSLabel>
                        <NSInputNumberStepper
                            name="stabilizedFreeRent"
                            value={operatingInfoData.stabilizedFreeRent || null}
                            onChange={(value: number) => updateOperatingInfoKeys({ stabilizedFreeRent: value })}
                            min={0}
                            step={1}
                            customSuffix={(operatingInfoData.stabilizedFreeRent || 0) > 1 ? 'months' : 'month'}
                            inputGroupClassName="OperatingInformation__input-width"
                        />
                    </div>
                </RenderIf>
            </Col>
            <Col className="px-1 pl-md-4 my-3 OperatingInformation__monthly-col" sm={12} md={true}>
                <div className="d-flex">
                    <RenderIf isTrue={!!operatingInfo.operationStartDate && !isStudentHousing}>
                        <NSCard className="NSCard--level-3 h-100 p-2 flex-fill mr-2 w-50">
                            <div className="d-flex align-items-center mb-0">
                                <span className="mr-1">Operation start</span>
                                <IconWithTooltip
                                    name="operationStart"
                                    icon={IconInfoCircleFilled}
                                    tooltipText={OPERATION_START_TOOLTIP}
                                />
                            </div>
                            <NSSelect
                                name="operationStartDate"
                                options={operationStartDateOptions}
                                value={operationStartDateOptions.find(option => {
                                    if (!operatingInfo?.operationStartDate) return false;
                                    const optDate = parseISO(option.value);
                                    const infoDate = parseISO(operatingInfo.operationStartDate);
                                    return optDate.getFullYear() === infoDate.getFullYear()
                                        && optDate.getMonth() === infoDate.getMonth();
                                })}
                                onChange={handleOperationStartDateSelect}
                                placeholder={operationStartDatePlaceholder}
                                isClearable={false}
                                isSearchable
                                menuPortalTarget={document.body}
                                formClassName="OperatingInformation__select-width"
                                styles={{
                                    ...customStyles,
                                    menuPortal: (provided: any) => ({ ...provided, zIndex: 10 }),
                                }}
                            />
                            <RenderIf isTrue={showOpsDuringConstructionWarning}>
                                <small className="text-muted mt-1 d-block">
                                    {OPS_DURING_CONSTRUCTION_WARNING}
                                </small>
                            </RenderIf>
                        </NSCard>
                    </RenderIf>
                    <RenderIf isTrue={!operatingInfo.operationStartDate && !isStudentHousing}>
                        <div className='flex-fill mr-2 w-50'>
                            <MetricOutputCard
                                label="Operation start"
                                value=""
                                showInProgress
                                showProjectActualsIcon={shouldUseProjectActuals
                                    && actualsDiscrepancies?.includes(ProjectActualsDiscrepanciesEnum.OPERATION_START_DATE)}
                                projectActualsIconName={ProjectActualsDiscrepanciesEnum.OPERATION_START_DATE}
                                inProgressText={(
                                    <span>
                                        In progress - provide start/end dates on
                                        {' '}
                                        <span
                                            role="presentation"
                                            onClick={redirectToDevelopmentTab}
                                            className="text-primary cursor--pointer"
                                        >
                                            Development: Uses
                                        </span>
                                        .
                                    </span>
                                )}
                            />
                        </div>
                    </RenderIf>
                    <RenderIf isTrue={isStudentHousing}>
                        <div className="flex-fill w-50 mr-2">
                            <MetricOutputCard
                                label="Lease term"
                                value="12 months"
                            />
                        </div>
                    </RenderIf>
                    <div className="flex-fill w-50">
                        <MetricOutputCard
                            label="1st stabilized month"
                            value={operatingInfo.stabilizedDate
                                ? `${format(parseISO(operatingInfo.stabilizedDate), 'MMM yyyy')} (Month ${operatingInfo.stabilizedMonth})` : ''}
                            showInProgress={!operatingInfo.stabilizedDate}
                            inProgressText={(
                                <span>
                                    In progress - provide operating information and
                                    {' '}
                                    {stabilizedMonthMissingEntity}
                                    .
                                </span>
                            )}
                        />
                    </div>
                </div>
                <MonthlyAnalysisTable
                    proFormaModelId={proForma.proFormaModelId}
                    monthlyAnalysisPeriods={monthlyAnalysisPeriods}
                    operationBeginMonth={operatingInfo.operationBeginMonth}
                />
            </Col>
        </Row>
    );
};

export default OperatingInformation;
